/**
 * Prozedurale Texturen.
 *
 * Fenster, Putz und Beschriftungen werden gezeichnet, nicht modelliert. Das hat einen
 * handfesten Grund: jedes zusaetzliche Geometrieteil an einer Fassade wuerde ueber die
 * Grundflaeche hinausragen, und genau das darf nicht passieren (Abnahmekriterium A).
 * Eine Textur kann nicht ueberstehen.
 */
import * as THREE from 'three'

function leinwand(breite: number, hoehe: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas')
  c.width = breite
  c.height = hoehe
  const ctx = c.getContext('2d')
  if (!ctx) throw new Error('Kein 2D-Kontext verfuegbar')
  return [c, ctx]
}

function ausLeinwand(c: HTMLCanvasElement, wiederholung?: [number, number]): THREE.Texture {
  const t = new THREE.CanvasTexture(c)
  t.colorSpace = THREE.SRGBColorSpace
  t.anisotropy = 8
  if (wiederholung) {
    t.wrapS = THREE.RepeatWrapping
    t.wrapT = THREE.RepeatWrapping
    t.repeat.set(wiederholung[0], wiederholung[1])
  }
  return t
}

const zwischenlager = new Map<string, THREE.Texture>()

function gemerkt(schluessel: string, bauen: () => THREE.Texture): THREE.Texture {
  const vorhanden = zwischenlager.get(schluessel)
  if (vorhanden) return vorhanden
  const neu = bauen()
  zwischenlager.set(schluessel, neu)
  return neu
}

/** Eine Fassade mit Fensterraster. */
export function fassade(
  wand: string,
  fenster: string,
  spalten: number,
  reihen: number,
  rahmen = 0.22,
): THREE.Texture {
  const schluessel = `fassade|${wand}|${fenster}|${spalten}|${reihen}|${rahmen}`
  return gemerkt(schluessel, () => {
    const px = 512
    const [c, ctx] = leinwand(px, px)
    ctx.fillStyle = wand
    ctx.fillRect(0, 0, px, px)

    // Feiner Putz, damit die Wand nicht wie eine Farbflaeche aussieht.
    const rauschen = ctx.getImageData(0, 0, px, px)
    for (let i = 0; i < rauschen.data.length; i += 4) {
      const d = (((i / 4) * 2654435761) % 17) - 8
      rauschen.data[i] = Math.max(0, Math.min(255, rauschen.data[i]! + d))
      rauschen.data[i + 1] = Math.max(0, Math.min(255, rauschen.data[i + 1]! + d))
      rauschen.data[i + 2] = Math.max(0, Math.min(255, rauschen.data[i + 2]! + d))
    }
    ctx.putImageData(rauschen, 0, 0)

    const zellB = px / spalten
    const zellH = px / reihen
    const randB = zellB * rahmen
    const randH = zellH * rahmen
    for (let sp = 0; sp < spalten; sp++) {
      for (let r = 0; r < reihen; r++) {
        const x = sp * zellB + randB
        const y = r * zellH + randH
        const b = zellB - randB * 2
        const h = zellH - randH * 2
        ctx.fillStyle = fenster
        ctx.fillRect(x, y, b, h)
        // Glanzkante oben links, damit die Scheibe Tiefe bekommt.
        ctx.fillStyle = 'rgba(255,255,255,0.20)'
        ctx.fillRect(x, y, b, Math.max(1, h * 0.16))
        ctx.fillStyle = 'rgba(0,0,0,0.16)'
        ctx.fillRect(x, y + h - Math.max(1, h * 0.1), b, Math.max(1, h * 0.1))
      }
    }
    return ausLeinwand(c)
  })
}

/** Ein Dach mit Ziegel- oder Schindelstruktur. */
export function dachhaut(farbe: string, dunkler: string, reihen = 14): THREE.Texture {
  return gemerkt(`dach|${farbe}|${dunkler}|${reihen}`, () => {
    const px = 256
    const [c, ctx] = leinwand(px, px)
    ctx.fillStyle = farbe
    ctx.fillRect(0, 0, px, px)
    const h = px / reihen
    ctx.strokeStyle = dunkler
    ctx.lineWidth = Math.max(1, h * 0.12)
    for (let i = 1; i < reihen; i++) {
      ctx.beginPath()
      ctx.moveTo(0, i * h)
      ctx.lineTo(px, i * h)
      ctx.stroke()
    }
    return ausLeinwand(c)
  })
}

/** Rasenflaeche mit unruhiger Struktur, fuer Freizeitparks und Gruenanlagen. */
export function rasen(hell: string, dunkel: string): THREE.Texture {
  return gemerkt(`rasen|${hell}|${dunkel}`, () => {
    const px = 256
    const [c, ctx] = leinwand(px, px)
    ctx.fillStyle = hell
    ctx.fillRect(0, 0, px, px)
    ctx.fillStyle = dunkel
    let saat = 7
    for (let i = 0; i < 2600; i++) {
      saat = (saat * 1103515245 + 12345) & 0x7fffffff
      const x = (saat >> 7) % px
      const y = (saat >> 15) % px
      ctx.fillRect(x, y, 2, 2)
    }
    return ausLeinwand(c)
  })
}

/** Asphalt mit feiner Koernung. */
export function asphalt(): THREE.Texture {
  return gemerkt('asphalt', () => {
    const px = 256
    const [c, ctx] = leinwand(px, px)
    ctx.fillStyle = '#2f333c'
    ctx.fillRect(0, 0, px, px)
    let saat = 13
    for (let i = 0; i < 6000; i++) {
      saat = (saat * 1103515245 + 12345) & 0x7fffffff
      const x = (saat >> 7) % px
      const y = (saat >> 16) % px
      const g = 40 + ((saat >> 3) % 26)
      ctx.fillStyle = `rgb(${g},${g + 2},${g + 6})`
      ctx.fillRect(x, y, 1, 1)
    }
    return ausLeinwand(c, [1, 1])
  })
}

/** Gehwegplatten. */
export function pflaster(): THREE.Texture {
  return gemerkt('pflaster', () => {
    const px = 128
    const [c, ctx] = leinwand(px, px)
    ctx.fillStyle = '#cfcabc'
    ctx.fillRect(0, 0, px, px)
    ctx.strokeStyle = '#b3ad9d'
    ctx.lineWidth = 2
    for (let i = 1; i < 4; i++) {
      ctx.beginPath()
      ctx.moveTo(0, (i * px) / 4)
      ctx.lineTo(px, (i * px) / 4)
      ctx.stroke()
      ctx.beginPath()
      ctx.moveTo((i * px) / 4, 0)
      ctx.lineTo((i * px) / 4, px)
      ctx.stroke()
    }
    return ausLeinwand(c, [1, 1])
  })
}

/**
 * Untergrund eines Hotelgelaendes: fast weiss, mit feiner Koernung und blassen
 * Parzellenlinien. Wird ueber `material.color` eingefaerbt, damit eine Textur fuer
 * alle Gelaende reicht.
 */
export function grund(): THREE.Texture {
  return gemerkt('grund', () => {
    const px = 256
    const [c, ctx] = leinwand(px, px)
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, px, px)
    let saat = 29
    for (let i = 0; i < 5200; i++) {
      saat = (saat * 1103515245 + 12345) & 0x7fffffff
      const x = (saat >> 7) % px
      const y = (saat >> 16) % px
      const g = 228 + ((saat >> 3) % 28)
      ctx.fillStyle = `rgb(${g},${g},${g})`
      ctx.fillRect(x, y, 2, 2)
    }
    ctx.strokeStyle = 'rgba(0,0,0,0.05)'
    ctx.lineWidth = 1
    for (let i = 0; i < px; i += 32) {
      ctx.beginPath()
      ctx.moveTo(i, 0)
      ctx.lineTo(i, px)
      ctx.stroke()
      ctx.beginPath()
      ctx.moveTo(0, i)
      ctx.lineTo(px, i)
      ctx.stroke()
    }
    return ausLeinwand(c, [1, 1])
  })
}

/** Beschriftung als Textur, weiss auf durchsichtig. */
export function schriftzug(
  text: string,
  farbe = '#ffffff',
  breite = 512,
  hoehe = 128,
): THREE.Texture {
  return gemerkt(`schrift|${text}|${farbe}|${breite}|${hoehe}`, () => {
    const [c, ctx] = leinwand(breite, hoehe)
    ctx.clearRect(0, 0, breite, hoehe)
    ctx.font = `700 ${Math.round(hoehe * 0.62)}px "Segoe UI", system-ui, sans-serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.lineJoin = 'round'
    ctx.lineWidth = hoehe * 0.14
    ctx.strokeStyle = 'rgba(0,0,0,0.55)'
    ctx.strokeText(text, breite / 2, hoehe / 2)
    ctx.fillStyle = farbe
    ctx.fillText(text, breite / 2, hoehe / 2)
    const t = ausLeinwand(c)
    t.generateMipmaps = true
    return t
  })
}

export type Feldsymbol = 'kauf' | 'bau' | 'eingang-gratis' | 'bauphase-gratis' | 'start'

/** Zeichnet ein Feldsymbol in einen 128x128-Kontext. */
function zeichneFeldsymbol(ctx: CanvasRenderingContext2D, art: Feldsymbol): void {
  ctx.clearRect(0, 0, 128, 128)
  ctx.lineWidth = 6
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'

  if (art === 'kauf') {
    // Drei Muenzen: hier wird gekauft.
    ctx.fillStyle = '#f3c34a'
    ctx.strokeStyle = '#8a6410'
    for (const [x, y, r] of [
      [46, 78, 22],
      [82, 78, 22],
      [64, 50, 22],
    ] as const) {
      ctx.beginPath()
      ctx.arc(x, y, r, 0, Math.PI * 2)
      ctx.fill()
      ctx.stroke()
    }
  } else if (art === 'bau') {
    // Bauplan mit Winkel: hier wird die Genehmigung beantragt.
    ctx.fillStyle = '#e8eef7'
    ctx.strokeStyle = '#39506e'
    ctx.beginPath()
    ctx.roundRect(24, 26, 80, 76, 6)
    ctx.fill()
    ctx.stroke()
    ctx.strokeStyle = '#39506e'
    ctx.lineWidth = 5
    ctx.beginPath()
    ctx.moveTo(38, 88)
    ctx.lineTo(38, 46)
    ctx.lineTo(76, 46)
    ctx.stroke()
    ctx.beginPath()
    ctx.moveTo(50, 88)
    ctx.lineTo(50, 60)
    ctx.lineTo(90, 60)
    ctx.lineTo(90, 88)
    ctx.stroke()
  } else if (art === 'eingang-gratis') {
    // Vordach mit Stufen: ein Eingang geschenkt.
    ctx.fillStyle = '#f6d24b'
    ctx.strokeStyle = '#7a5a06'
    ctx.beginPath()
    ctx.moveTo(20, 88)
    ctx.lineTo(40, 52)
    ctx.lineTo(88, 52)
    ctx.lineTo(108, 88)
    ctx.closePath()
    ctx.fill()
    ctx.stroke()
    ctx.beginPath()
    ctx.moveTo(46, 52)
    ctx.lineTo(64, 30)
    ctx.lineTo(82, 52)
    ctx.closePath()
    ctx.fill()
    ctx.stroke()
  } else if (art === 'bauphase-gratis') {
    // Kran ueber Grundriss: eine Bauphase geschenkt.
    ctx.strokeStyle = '#c1553a'
    ctx.lineWidth = 7
    ctx.beginPath()
    ctx.moveTo(40, 100)
    ctx.lineTo(40, 30)
    ctx.lineTo(96, 30)
    ctx.stroke()
    ctx.beginPath()
    ctx.moveTo(96, 30)
    ctx.lineTo(96, 52)
    ctx.stroke()
    ctx.fillStyle = '#3d6f4a'
    ctx.fillRect(58, 74, 46, 26)
  } else {
      // START: Zielflagge.
    const feld = 16
    for (let x = 0; x < 8; x++) {
      for (let y = 0; y < 4; y++) {
        ctx.fillStyle = (x + y) % 2 === 0 ? '#1c1f26' : '#f4f5f7'
        ctx.fillRect(x * feld, 32 + y * feld, feld, feld)
      }
    }
  }
}

/** Das Symbol eines Strassenfeldes als Textur fuer das Brett. */
export function feldsymbol(art: Feldsymbol): THREE.Texture {
  return gemerkt(`feld|${art}`, () => {
    const [c, ctx] = leinwand(128, 128)
    zeichneFeldsymbol(ctx, art)
    return ausLeinwand(c)
  })
}

/** Dasselbe Symbol als Bilddatei, fuer die Regelhilfe in der Oberflaeche. */
export function feldsymbolBild(art: Feldsymbol): string {
  const [c, ctx] = leinwand(128, 128)
  zeichneFeldsymbol(ctx, art)
  return c.toDataURL('image/png')
}
